import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function Home() {
  const [sermons, setSermons] = useState([]);
  const [markPosts, setMarkPosts] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [latestComments, setLatestComments] = useState([]);
  const [noticePosts, setNoticePosts] = useState([]);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [keepSigned, setKeepSigned] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    // 로그인 상태 확인
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // API를 통해 게시판 데이터 가져오기
    const fetchBoard = async (board) => {
      try {
        // 로컬 개발: localhost:3001, 프로덕션: Vercel Serverless
        const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
        const apiUrl = `${apiBase}/api/board/${board}?page=1&limit=5`;
        const response = await fetch(apiUrl);
        const data = await response.json();

        // 응답 구조 확인
        const postsArray = data.posts || [];

        return postsArray.map(item => {
          // XE 날짜 형식: 20040630210542 -> MM/DD
          const dateStr = item.regdate || '';
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          return {
            id: item.document_srl,
            title: item.title,
            content: item.content,
            author: item.nick_name || item.user_name,
            date: `${month}/${day}`,
            rawDate: dateStr, // Badge logic
            lastUpdate: item.last_update || dateStr, // For U badge
            board_id: board
          };
        });
      } catch (error) {
        console.error(`Board fetch error for ${board}:`, error);
        return [];
      }
    };

    // 최신 댓글 데이터 가져오기
    const fetchLatestComments = async () => {
      try {
        const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
        const response = await fetch(`${apiBase}/api/comments/latest`);
        const data = await response.json();

        if (data.comments) {
          const formattedComments = data.comments.map(item => {
            const dateStr = item.regdate || '';
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            return {
              id: item.comment_srl,
              document_srl: item.document_srl,
              content: item.content,
              author: item.nick_name || item.user_name,
              date: `${month}/${day}`,
              board_id: item.board_id,
              document_title: item.document_title
            };
          }).slice(0, 10);
          setLatestComments(formattedComments);
        }
      } catch (error) {
        console.error('Latest comments fetch error:', error);
      }
    };

    // 각 게시판 데이터 가져오기
    Promise.all([
      fetchBoard('sermon'),   // 설교모음
      fetchBoard('mark'),     // 매일묵상
      fetchBoard('notice'),   // 알림방
      fetchBoard('member'),   // 후원안내 (Support Guide)
    ]).then(([sermonData, markData, noticeData, memberData]) => {
      console.log('Sermon data:', sermonData);
      console.log('Mark data:', markData);
      console.log('Notice data:', noticeData);
      console.log('Member data:', memberData);

      setSermons(sermonData.slice(0, 5));
      setMarkPosts(markData.slice(0, 5));
      setNoticePosts(noticeData.slice(0, 5));

      // Merge all for Latest Posts (Sermon + Mark + Member) - excluding Notice as per request mostly implying main content
      // User said: "후원안내 게시판의 글이 최신글에 자동으로 업로드되게 해주세요"
      const allLatest = [...sermonData, ...markData, ...memberData];

      // Sort by rawDate descending
      allLatest.sort((a, b) => {
        if (a.rawDate < b.rawDate) return 1;
        if (a.rawDate > b.rawDate) return -1;
        return 0;
      });

      setLatestPosts(allLatest.slice(0, 10));
    });

    fetchLatestComments();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      // 로컬 개발 환경에서는 server.js 사용, 프로덕션에서는 Vercel Serverless Function 사용
      const apiUrl = process.env.REACT_APP_API_URL
        ? `${process.env.REACT_APP_API_URL}/api/login`
        : `/api/login`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '로그인 실패');
      }

      // 토큰과 사용자 정보 저장
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);

      // 폼 초기화
      setUserId('');
      setPassword('');
      setLoginError('');

      // 페이지 새로고침으로 헤더 업데이트
      window.location.reload();
    } catch (err) {
      setLoginError(err.message || '로그인에 실패했습니다.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.reload();
  };

  // Badge Logic
  const isNew = (dateStr) => {
    if (!dateStr || dateStr.length < 8) return false;
    // Format: YYYYMMDDHHMMSS
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    const hour = parseInt(dateStr.substring(8, 10) || 0);
    const minute = parseInt(dateStr.substring(10, 12) || 0);
    const second = parseInt(dateStr.substring(12, 14) || 0);

    const postDate = new Date(year, month, day, hour, minute, second);
    const now = new Date();
    const diff = (now - postDate) / (1000 * 60 * 60); // hours
    return diff < 48; // 48 hours for 'New'
  };

  // Update Badge Logic - 글이 수정된 경우 (last_update > regdate) AND 최근 3일 이내
  const isUpdated = (regdate, lastUpdate) => {
    if (!regdate || !lastUpdate) return false;
    if (regdate === lastUpdate) return false;
    // last_update가 regdate보다 크면 수정된 글
    if (lastUpdate <= regdate) return false;

    // 최근 3일(72시간) 이내에 수정된 경우에만 U마크 표시
    if (!lastUpdate || lastUpdate.length < 8) return false;
    const year = parseInt(lastUpdate.substring(0, 4));
    const month = parseInt(lastUpdate.substring(4, 6)) - 1;
    const day = parseInt(lastUpdate.substring(6, 8));
    const hour = parseInt(lastUpdate.substring(8, 10) || 0);
    const minute = parseInt(lastUpdate.substring(10, 12) || 0);
    const second = parseInt(lastUpdate.substring(12, 14) || 0);

    const updateDate = new Date(year, month, day, hour, minute, second);
    const now = new Date();
    const diffHours = (now - updateDate) / (1000 * 60 * 60);
    return diffHours < 72; // 72시간 = 3일
  };

  return (
    <div id="contentBody">
      {/* Left Column */}
      <div id="columnLeft">
        {/* Login Widget */}
        <div id="login">
          <fieldset>
            <legend>로그인</legend>
            {user ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <strong>{user.nick_name}님</strong>
                <p style={{ margin: '10px 0', fontSize: '14px', color: '#666' }}>
                  환영합니다!
                </p>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  로그아웃
                </button>
              </div>
            ) : (
              <form onSubmit={handleLogin}>
                <strong>다비아 로그인</strong>
                {loginError && (
                  <p style={{ color: '#f44336', fontSize: '12px', margin: '10px 0' }}>
                    {loginError}
                  </p>
                )}
                <div className="idpw">
                  <input
                    name="user_id"
                    type="text"
                    placeholder="아이디"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    required
                  />
                  <input
                    name="password"
                    type="password"
                    placeholder="비밀번호"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <input type="submit" value="로그인" className="login" />
                <p className="save">
                  <input
                    type="checkbox"
                    name="keep_signed"
                    id="keepid"
                    value="Y"
                    checked={keepSigned}
                    onChange={(e) => setKeepSigned(e.target.checked)}
                  />
                  <label htmlFor="keepid">로그인 유지</label>
                </p>
              </form>
            )}
          </fieldset>
        </div>

        {/* 칼럼방 / 샘터교회 링크 */}
        <div id="sidebar-links" style={{ marginTop: '15px' }}>
          <div style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            <Link to="/columns" style={{ display: 'block', textAlign: 'center', color: '#333', fontWeight: 'bold', textDecoration: 'none' }}>
              📖 칼럼방
            </Link>
          </div>
          <div style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            <Link to="/church" style={{ display: 'block', textAlign: 'center', color: '#333', fontWeight: 'bold', textDecoration: 'none' }}>
              ⛪ 다비아샘터교회
            </Link>
          </div>
          <div style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            <Link to="/seoul-church" style={{ display: 'block', textAlign: 'center', color: '#333', fontWeight: 'bold', textDecoration: 'none' }}>
              ⛪ 서울샘터교회
            </Link>
          </div>
          <div style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            <a href="https://indiadabia.blogspot.com/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', color: '#333', fontWeight: 'bold', textDecoration: 'none' }}>
              🔗 설교링크
            </a>
          </div>
        </div>

        {/* 알림방 */}
        <div id="notice-board" style={{ marginTop: '15px' }}>
          <div style={{
            backgroundColor: '#748320',
            color: 'white',
            padding: '10px',
            borderRadius: '4px 4px 0 0',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>📢 알림방</span>
            <Link to="/board/notice" style={{ color: 'white', fontSize: '12px', textDecoration: 'none' }}>more</Link>
          </div>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            padding: '10px'
          }}>
            {noticePosts.length > 0 ? (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {noticePosts.map((post, idx) => (
                  <li key={idx} style={{
                    marginBottom: idx < noticePosts.length - 1 ? '12px' : '0',
                    paddingBottom: idx < noticePosts.length - 1 ? '12px' : '0',
                    borderBottom: idx < noticePosts.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <Link
                      to={`/board/notice/${post.id}`}
                      style={{
                        color: '#333',
                        textDecoration: 'none',
                        fontSize: '13px',
                        display: 'block',
                        lineHeight: '1.5'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {post.title.length > 30 ? post.title.substring(0, 30) + '...' : post.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
                        {(() => {
                          const content = post.description || post.content || '';
                          const tempDiv = document.createElement('div');
                          tempDiv.innerHTML = content;
                          let textOnly = tempDiv.textContent || tempDiv.innerText || '';
                          const cleanText = textOnly.replace(/\s+/g, ' ').trim();
                          return cleanText.length > 60 ? cleanText.substring(0, 60) + '...' : cleanText;
                        })()}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                        {post.date} | {post.author}
                        {isNew(post.rawDate) && <span className="badge-new">N</span>}
                        {isUpdated(post.rawDate, post.lastUpdate) && <span className="badge-update">U</span>}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: 0, padding: '20px 0', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                등록된 알림이 없습니다.
              </p>
            )}
          </div>
        </div>

      </div>

      {/* Center Column */}
      <div id="columnCenter">
        {/* Sermon Section */}
        <div id="sermon">
          <div className="nw_box">
            <h2>설교모음
              <Link to="/board/sermon" className="more">more</Link>
            </h2>
          </div>
          <div className="latest_white">
            {sermons.map((sermon, idx) => (
              <div key={idx}>
                {idx === 0 ? (
                  // 첫 번째 항목 - 제목과 미리보기
                  <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>
                      <Link to={`/board/sermon/${sermon.id}`} style={{ color: '#333', textDecoration: 'none' }}>
                        {sermon.title}
                      </Link>
                    </h3>
                    <p style={{ margin: '0', fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                      {(() => {
                        const content = sermon.description || sermon.content || '';

                        // DOM Parser를 사용해서 텍스트만 추출
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = content;
                        let textOnly = tempDiv.textContent || tempDiv.innerText || '';

                        // 연속된 공백과 줄바꿈 제거
                        const cleanText = textOnly.replace(/\s+/g, ' ').trim();

                        // 100자로 제한
                        return cleanText.length > 100 ? cleanText.substring(0, 100) + '...' : cleanText;
                      })()}
                    </p>
                  </div>
                ) : (
                  // 나머지 항목 - 제목만
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    <li>
                      <span className="date">[{sermon.date}]</span>
                      <span className="title">
                        <Link to={`/board/sermon/${sermon.id}`}>{sermon.title}</Link>
                        {isNew(sermon.rawDate) && <span className="badge-new">N</span>}
                        {isUpdated(sermon.rawDate, sermon.lastUpdate) && <span className="badge-update">U</span>}
                      </span>
                    </li>
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mark Posts Section (매일묵상) */}
        <div id="colum">
          <div className="nw_box">
            <h2>매일묵상
              <Link to="/board/mark" className="more">more</Link>
            </h2>
          </div>
          <div className="latest_white">
            {markPosts.map((post, idx) => (
              <div key={idx}>
                {idx === 0 ? (
                  // 첫 번째 항목 - 제목과 미리보기
                  <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>
                      <Link to={`/board/mark/${post.id}`} style={{ color: '#333', textDecoration: 'none' }}>
                        {post.title}
                      </Link>
                    </h3>
                    <p style={{ margin: '0', fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                      {(() => {
                        const content = post.description || post.content || '';

                        // DOM Parser를 사용해서 텍스트만 추출
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = content;
                        let textOnly = tempDiv.textContent || tempDiv.innerText || '';

                        // 연속된 공백과 줄바꿈 제거
                        const cleanText = textOnly.replace(/\s+/g, ' ').trim();

                        // 100자로 제한
                        return cleanText.length > 100 ? cleanText.substring(0, 100) + '...' : cleanText;
                      })()}
                    </p>
                  </div>
                ) : (
                  // 나머지 항목 - 제목만
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    <li>
                      <span className="date">[{post.date}]</span>
                      <span className="title">
                        <Link to={`/board/mark/${post.id}`}>{post.title}</Link>
                        {isNew(post.rawDate) && <span className="badge-new">N</span>}
                        {isUpdated(post.rawDate, post.lastUpdate) && <span className="badge-update">U</span>}
                      </span>
                    </li>
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div id="columnRight">
        {/* Latest Posts */}
        <div id="new-posting">
          <h2>전체 최신글</h2>
          <div className="latest_white">
            <ul>
              {latestPosts.map((post, idx) => {
                return (
                  <li key={idx}>
                    <span className="date">[{post.date}]</span>
                    <span className="title">
                      <Link to={`/board/${post.board_id}/${post.id}`}>{post.title.substring(0, 20)}...</Link>
                      {isNew(post.rawDate) && <span className="badge-new">N</span>}
                      {isUpdated(post.rawDate, post.lastUpdate) && <span className="badge-update">U</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Latest Comments */}
        <div id="new-comment" style={{ marginTop: '20px' }}>
          <h2>최신 댓글</h2>
          <div className="latest_white">
            <ul>
              {latestComments.map((comment, idx) => {
                // HTML 태그 제거 및 길이 제한
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = comment.content;
                let cleanText = (tempDiv.textContent || tempDiv.innerText || '').replace(/\s+/g, ' ').trim();
                if (cleanText.length > 40) cleanText = cleanText.substring(0, 40) + '...';

                return (
                  <li key={idx}>
                    <span className="date">[{comment.date}]</span>
                    <span className="title">
                      <Link to={`/board/${comment.board_id}/${comment.document_srl}`} title={comment.document_title}>
                        {cleanText} <span style={{ fontSize: '11px', color: '#999' }}>({comment.author})</span>
                      </Link>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
